import { useEffect, useState } from 'react';
import { publicApi } from '../api/client';

/**
 * Sous-services dynamiques : récupérés depuis l'API/DB.
 * Pas de fallback codé en dur — hors-ligne, ils sont indisponibles
 * (la commande en ligne est alors bloquée côté formulaire).
 */
export function useSubServices(parentSlug) {
  const [state, setState] = useState({ items: [], loading: !!parentSlug, offline: false });

  useEffect(() => {
    let alive = true;
    if (!parentSlug) {
      setState({ items: [], loading: false, offline: false });
      return () => { alive = false; };
    }
    setState((s) => ({ ...s, loading: true, offline: false }));
    publicApi.subServices(parentSlug)
      .then((res) => {
        if (alive) setState({ items: res.subServices || [], loading: false, offline: false });
      })
      .catch(() => {
        if (alive) setState({ items: [], loading: false, offline: true });
      });
    return () => { alive = false; };
  }, [parentSlug]);

  return state;
}

/**
 * Types de service depuis la base (collection `catalogtypes`).
 * Hors-ligne : indisponibles comme les sous-services.
 */
export function useCatalogTypes() {
  const [state, setState] = useState({ types: [], loading: true, offline: false });

  useEffect(() => {
    let alive = true;
    publicApi.catalog()
      .then((res) => {
        if (alive) setState({ types: res.types || [], loading: false, offline: false });
      })
      .catch(() => {
        if (alive) setState({ types: [], loading: false, offline: true });
      });
    return () => { alive = false; };
  }, []);

  return state;
}

/**
 * Services du catalogue (collection `catalogservices`) — titres, tarifs
 * (`prices`, `basePrice`, `unit`), images. Hors-ligne : liste vide.
 */
export function useCatalogServices() {
  const [state, setState] = useState({ services: [], loading: true, offline: false });

  useEffect(() => {
    let alive = true;
    publicApi.catalog()
      .then((res) => {
        if (alive) setState({ services: res.services || [], loading: false, offline: false });
      })
      .catch(() => {
        if (alive) setState({ services: [], loading: false, offline: true });
      });
    return () => { alive = false; };
  }, []);

  return state;
}

/**
 * Options de livraison actives (collection `catalogdeliveries`) —
 * libellés, descriptions, tarifs et tarifs par pays configurés par l'admin.
 * Hors-ligne : liste vide.
 */
export function useDeliveryOptions() {
  const [state, setState] = useState({ options: [], loading: true, offline: false });

  useEffect(() => {
    let alive = true;
    publicApi.catalog()
      .then((res) => {
        if (alive) setState({ options: res.deliveryOptions || [], loading: false, offline: false });
      })
      .catch(() => {
        if (alive) setState({ options: [], loading: false, offline: true });
      });
    return () => { alive = false; };
  }, []);

  return state;
}

/**
 * Langues actives du catalogue (collection `cataloglanguages`, importées
 * via API + activées/désactivées par l'admin).
 * Hors-ligne ou catalogue vide : liste vide — l'appelant garde son repli local.
 */
export function useCatalogLanguages() {
  const [state, setState] = useState({ languages: [], loading: true, offline: false });

  useEffect(() => {
    let alive = true;
    publicApi.catalog()
      .then((res) => {
        if (alive) setState({ languages: res.languages || [], loading: false, offline: false });
      })
      .catch(() => {
        if (alive) setState({ languages: [], loading: false, offline: true });
      });
    return () => { alive = false; };
  }, []);

  return state;
}
